import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { FileBarChart2, Download, AlertTriangle, Loader2, FileCode2, CheckCircle2, Save, Upload, Settings2, Users, CalendarCheck, ScrollText, Unlink } from 'lucide-react';
import { Tabs } from '../../components/ui';
import { useConfirm } from '../../components/ConfirmDialog';
import { PageHead, Panel, Register, FigureStrip, Figure, Field, FormRow, ErrorState, TableSkeleton, fmtDate } from './design';
import { DexConnection, DexSend, DexRequestLog } from './DexWebServices';
import DexConsents from './DexConsents';
import DexSessions from './DexSessions';
import DexMonths from './DexMonths';
import DexUpload from './DexUpload';

// DEX reporting for CHSP. Built to the Data Exchange Bulk File Upload Technical Specification v11
// and the CHSP Data Exchange Guide (July 2026):
//   0. upload the two files from the DEX portal's Reference page (every code and ID comes from them)
//   1. map each CHSP service to its outlet activity and DEX service type
//   2. fill in the participant details DEX needs that the client record does not hold
//   3. build the upload file for a date range and upload it in the DEX portal
// Or, with a DEX web service user, get the two files' contents from DEX and send the window straight
// to DEX (DexWebServices.jsx, routes/agedCareDexWs.js). Both report the same plan.
//
// The page is in tabs because it had grown to nine sections stacked one under another: set-up
// (done once), participants (fixed as they come), the monthly report (every month) and the request
// log. Every tab stays mounted and is only hidden, so the connection settings still decide whether
// sending is possible from the monthly report tab, and nothing typed is lost by changing tab.

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const lastMonth = () => { const n = new Date(); return [ymd(new Date(n.getFullYear(), n.getMonth() - 1, 1)), ymd(new Date(n.getFullYear(), n.getMonth(), 0))]; };
const halfYear = (first) => { const y = new Date().getFullYear(); return first ? [`${y}-01-01`, `${y}-06-30`] : [`${y}-07-01`, `${y}-12-31`]; };
const MEAL_CODES = ['MEALPROVIDED', 'MEALCOOKEDOFFSITE', 'ITEMSTEXTUREMODIFIED', 'MAINMEALCOUNT', 'LIGHTMEALCOUNT', 'DESSERTCOUNT', 'BEVERAGECOUNT', 'SNACKCOUNT'];
const REQ_LABEL = {
  ASSISTANCEMINUTES: 'Time', ASSISTANCEQUANTITY: 'Quantity', ASSISTANCECOSTAMOUNT: 'Cost', SUBCONTRACTORDELIVERED: 'Subcontractor',
  VOLUNTEERDIRECTSERVICE: 'Volunteer', SERVICELOCATIONSETTINGCODE: 'Location', OVERNIGHTSUPPORTINCLUDED: 'Overnight',
  MEALPROVIDED: 'Meal provided', MEALCOOKEDOFFSITE: 'Cooked offsite', ITEMSTEXTUREMODIFIED: 'Texture modified',
  MAINMEALCOUNT: 'Main meals', LIGHTMEALCOUNT: 'Light meals', DESSERTCOUNT: 'Desserts', BEVERAGECOUNT: 'Beverages', SNACKCOUNT: 'Snacks',
  ENGAGEMENTTIME: 'Engagement time', TRIPDISTANCE: 'Trip distance', TRIPTIME: 'Trip time',
};
const EMPTY = { files: {}, activities: [], outlets: [], lists: {}, clients: [], serviceTypes: [], requiredClientFields: [] };

function CodeSelect({ label, value, onChange, items, placeholder = 'Not recorded', hint }) {
  return (
    <Field label={label} hint={hint}>
      {(id) => (
        <select id={id} className="input-field" value={value || ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">{items?.length ? placeholder : 'Upload the reference data file first'}</option>
          {(items || []).map((x) => <option key={x.code} value={x.code}>{x.desc || 'Not named'}</option>)}
        </select>
      )}
    </Field>
  );
}

function FileUpload({ kind, label, uploaded, detail, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const send = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('kind', kind); fd.append('file', file);
      await api.post('/aged-care/dex/files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${label} loaded`); onDone();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not read that file'); }
    finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2">
      <div className="min-w-0">
        <div className="text-[13px] font-medium text-slate-800">{label}</div>
        <div className="text-[12px] text-slate-500">{uploaded ? `Loaded ${fmtDate(uploaded)}${detail ? `, ${detail}` : ''}` : 'Not loaded yet'}</div>
      </div>
      <input ref={ref} type="file" accept=".xml,text/xml,application/xml" className="hidden" onChange={(e) => send(e.target.files?.[0])} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className={uploaded ? 'btn-secondary btn-sm' : 'btn-primary btn-sm'}>
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {uploaded ? 'Replace' : 'Upload'}
      </button>
    </div>
  );
}

function MappingRow({ t, outlets, activities, lists, onSaved }) {
  const confirm = useConfirm();
  const [f, setF] = useState({
    outlet: t.outlet_activity_id ? String(t.outlet_activity_id) : '', svc: t.dex_service_type_id ? String(t.dex_service_type_id) : '',
    options: t.options || {},
  });
  const [saving, setSaving] = useState(false);
  const outlet = outlets.find((o) => String(o.outletActivityId) === f.outlet);
  const activity = outlet ? activities.find((a) => a.id === outlet.organisationActivityId) : null;
  const st = activity ? activity.serviceTypes.find((x) => String(x.id) === f.svc) : null;
  const req = new Set(st?.requirements || []);
  const opt = (k, v) => setF((p) => ({ ...p, options: { ...p.options, [k]: v } }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/aged-care/dex/mapping/${t.id}`, { outletActivityId: f.outlet, dexServiceTypeId: f.svc, options: f.options });
      toast.success(`${t.name} mapped`); onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the mapping'); } finally { setSaving(false); }
  };
  // A service mapped by mistake (or no longer CHSP) used to stay in every file with no way back.
  const unmap = async () => {
    const ok = await confirm(`${t.name} visits are left out of the DEX file and of sending by web services until it is mapped again. Sessions already reported stay in DEX.`,
      { title: `Take ${t.name} out of DEX?`, confirmLabel: 'Take out of DEX', danger: true });
    if (!ok) return;
    setSaving(true);
    try {
      await api.delete(`/aged-care/dex/mapping/${t.id}`);
      setF((p) => ({ ...p, outlet: '', svc: '' }));
      toast.success(`${t.name} taken out of DEX`); onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not take it out of DEX'); } finally { setSaving(false); }
  };
  const num = (k, label) => (
    <Field label={label}>{(id) => <input id={id} type="number" min="0" className="input-field w-24" value={f.options[k] ?? ''} onChange={(e) => opt(k, e.target.value)} />}</Field>
  );
  const yes = (k, label) => (
    <label className="flex items-center gap-2 text-[12.5px] text-slate-600 mt-5">
      <input type="checkbox" checked={!!f.options[k]} onChange={(e) => opt(k, e.target.checked)} /> {label}
    </label>
  );
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <span className="font-medium text-slate-800">{t.name}</span>
          <span className="ml-2 text-[11.5px] text-slate-500">{t.sessions} session{t.sessions === 1 ? '' : 's'} in the last year</span>
        </div>
        {t.outlet_activity_id ? <span className="text-[12px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={13} /> Mapped</span> : <span className="text-[12px] text-amber-700">Not mapped</span>}
      </div>
      <FormRow cols={2}>
        <Field label="Outlet activity (CHSP service type)">
          {(id) => (
            <select id={id} className="input-field" value={f.outlet} onChange={(e) => setF((p) => ({ ...p, outlet: e.target.value, svc: '' }))}>
              <option value="">{outlets.length ? 'Choose' : 'Upload the organisation activity file first'}</option>
              {outlets.map((o) => <option key={o.outletActivityId} value={o.outletActivityId}>{o.outletName || 'Outlet'}, {o.activityName || 'activity not named'}</option>)}
            </select>
          )}
        </Field>
        <Field label="DEX service type (CHSP service)">
          {(id) => (
            <select id={id} className="input-field" value={f.svc} onChange={(e) => setF((p) => ({ ...p, svc: e.target.value }))} disabled={!activity}>
              <option value="">Choose</option>
              {(activity?.serviceTypes || []).map((x) => <option key={x.id} value={x.id}>{x.name || 'Not named'}</option>)}
            </select>
          )}
        </Field>
      </FormRow>
      {st && (
        <div className="flex flex-wrap gap-1.5">
          {st.requirements.map((r) => <span key={r} className="badge badge-gray">{REQ_LABEL[r] || 'Other detail'}</span>)}
          {activity.requirements.includes('FEESCHARGEDSESSIONMANDT') && <span className="badge badge-blue">Fees charged</span>}
        </div>
      )}
      {st && (
        <div className="flex flex-wrap items-end gap-3">
          {req.has('SERVICELOCATIONSETTINGCODE') && (
            <CodeSelect label="Session location" value={f.options.locationCode} onChange={(v) => opt('locationCode', v)} items={lists.ServiceLocationSetting} placeholder="Choose" />
          )}
          {req.has('ASSISTANCEQUANTITY') && num('quantityPerSession', 'Quantity per visit')}
          {req.has('ASSISTANCECOSTAMOUNT') && (
            <Field label="Cost per visit ($)" hint="Blank uses the amount billed.">{(id) => <input id={id} type="number" min="0" className="input-field w-28" value={f.options.costPerSession ?? ''} onChange={(e) => opt('costPerSession', e.target.value)} />}</Field>
          )}
          {req.has('MEALPROVIDED') && (
            <label className="flex items-center gap-2 text-[12.5px] text-slate-600 mt-5">
              <input type="checkbox" checked={f.options.mealProvided !== false} onChange={(e) => opt('mealProvided', e.target.checked)} /> A meal is provided
            </label>
          )}
          {req.has('MEALCOOKEDOFFSITE') && yes('mealCookedOffsite', 'Cooked offsite')}
          {req.has('ITEMSTEXTUREMODIFIED') && yes('textureModified', 'Texture modified')}
          {req.has('MAINMEALCOUNT') && num('mainMeals', 'Main meals')}
          {req.has('LIGHTMEALCOUNT') && num('lightMeals', 'Light meals')}
          {req.has('DESSERTCOUNT') && num('desserts', 'Desserts')}
          {req.has('BEVERAGECOUNT') && num('beverages', 'Beverages')}
          {req.has('SNACKCOUNT') && num('snacks', 'Snacks')}
          {MEAL_CODES.some((c) => req.has(c)) && <p className="w-full text-[11.5px] text-slate-500">Meal fields are per visit, for each participant.</p>}
          {req.has('ENGAGEMENTTIME') && num('engagementMinutes', 'Engagement time (min)')}
          {(req.has('TRIPDISTANCE') || req.has('TRIPTIME')) && <p className="w-full text-[11.5px] text-slate-500">Trip distance comes from the kilometres on the worker's timesheet and trip time from the visit; change one on the session if it differs.</p>}
          <label className="w-full flex items-center gap-2 text-[12.5px] text-slate-600">
            <input type="checkbox" checked={f.options.groupSessions !== false} onChange={(e) => opt('groupSessions', e.target.checked)} />
            A visit with 3 or more CHSP participants is one group session (otherwise each participant is a session of their own)
          </label>
        </div>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        {t.outlet_activity_id && <button type="button" onClick={unmap} disabled={saving} className="btn-ghost btn-sm"><Unlink size={13} /> Take out of DEX</button>}
        <button type="button" onClick={save} disabled={saving || !f.outlet || !f.svc} className="btn-primary btn-sm"><Save size={13} /> Save</button>
      </div>
    </div>
  );
}

// A branch with its own DEX outlet: its visits are reported under the same activity at that outlet.
function BranchOutlets({ outlets }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get('/aged-care/dex/branch-outlets').then((r) => setRows(r.data || [])).catch(() => setRows([])); }, []);
  if (!rows.length) return null;
  const save = async (r, outletId, naps) => {
    try {
      await api.put(`/aged-care/dex/branch-outlets/${r.location_id}`, { outletId, napsServiceId: naps });
      setRows((p) => p.map((x) => (x.location_id === r.location_id ? { ...x, outlet_id: outletId || null, naps_service_id: naps } : x)));
      toast.success(`${r.name} saved`);
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2">
      <p className="text-[13px] font-medium text-slate-800">Branches and DEX outlets</p>
      <p className="text-[12px] text-slate-500">A branch with its own DEX outlet reports its visits under the same activity at that outlet. Others use the outlet chosen for the service.</p>
      {rows.map((r) => (
        <div key={r.location_id} className="flex flex-wrap items-center gap-2 text-[13px]">
          <span className="w-40 truncate text-slate-700">{r.name}</span>
          <select aria-label={`DEX outlet for ${r.name}`} className="input-field !w-auto !py-1" value={r.outlet_id || ''} onChange={(e) => save(r, e.target.value ? Number(e.target.value) : null, r.naps_service_id || '')}>
            <option value="">The service's outlet</option>
            {outlets.map((o) => <option key={o.outletId} value={o.outletId}>{o.name || 'Outlet'}</option>)}
          </select>
          <input aria-label={`NAPS service ID for ${r.name}`} className="input-field !w-40 !py-1" placeholder="NAPS service ID" defaultValue={r.naps_service_id || ''}
            onBlur={(e) => { if ((e.target.value || '') !== (r.naps_service_id || '')) save(r, r.outlet_id || null, e.target.value); }} />
        </div>
      ))}
    </div>
  );
}

function ClientFix({ c, lists, required, onSaved, onCancel }) {
  const [f, setF] = useState({ ...c.fields });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const disab = new Set(String(f.disabilityCodes || '').split(',').filter(Boolean));
  const toggleDisab = (code) => { const n = new Set(disab); if (n.has(code)) n.delete(code); else n.add(code); setF((p) => ({ ...p, disabilityCodes: [...n].join(',') })); };
  const yn = (v) => (v == null || v === '' ? '' : v ? 'yes' : 'no');
  const save = async () => {
    setSaving(true);
    try { await api.put(`/aged-care/dex/client/${c.id}`, f); toast.success('Saved'); onSaved(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save'); } finally { setSaving(false); }
  };
  const req = new Set(required);
  return (
    <div className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
      <FormRow cols={2}>
        <CodeSelect label={`Country of birth${c.country_of_birth ? ` (on file: ${c.country_of_birth})` : ''}`} value={f.countryCode} onChange={set('countryCode')} items={lists.Country} placeholder="Match from the client record" />
        <CodeSelect label={`Language spoken at home${c.languages_spoken ? ` (on file: ${String(c.languages_spoken).split(/[,;/]/)[0]})` : ''}`} value={f.languageCode} onChange={set('languageCode')} items={lists.Language} placeholder="Match from the client record" />
      </FormRow>
      <FormRow cols={2}>
        <Field label={`Registered for My Aged Care${c.mac ? ` (ID on file: ${c.mac})` : ''}`} hint="DEX needs the My Aged Care ID when they are registered, and none when they are not.">
          {(id) => (
            <select id={id} className="input-field" value={f.macRegistered || ''} onChange={(e) => set('macRegistered')(e.target.value)}>
              <option value="">Not answered (from the ID on file)</option><option value="yes">Yes</option><option value="no">No</option>
            </select>
          )}
        </Field>
      </FormRow>
      <FormRow cols={2}>
        <Field label="Has a disability">
          {(id) => (
            <select id={id} className="input-field" value={yn(f.hasDisability)} onChange={(e) => set('hasDisability')(e.target.value === '' ? null : e.target.value === 'yes')}>
              <option value="">Not recorded</option><option value="no">No</option><option value="yes">Yes</option>
            </select>
          )}
        </Field>
        {(req.has('HASCARER')) && (
          <Field label="Has a carer">
            {(id) => (
              <select id={id} className="input-field" value={yn(f.hasCarer)} onChange={(e) => set('hasCarer')(e.target.value === '' ? null : e.target.value === 'yes')}>
                <option value="">Not recorded</option><option value="no">No</option><option value="yes">Yes</option>
              </select>
            )}
          </Field>
        )}
      </FormRow>
      {f.hasDisability === true && (
        <div>
          <div className="text-[12px] text-slate-600 mb-1">Disability types</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {(lists.Disability || []).map((d) => (
              <label key={d.code} className="flex items-center gap-1.5 text-[12.5px] text-slate-700"><input type="checkbox" checked={disab.has(d.code)} onChange={() => toggleDisab(d.code)} /> {d.desc || 'Not named'}</label>
            ))}
          </div>
        </div>
      )}
      <FormRow cols={2}>
        {req.has('ACCOMMODATIONTYPE') && <CodeSelect label="Accommodation type" value={f.accommodationCode} onChange={set('accommodationCode')} items={lists.AccommodationType} />}
        {req.has('DVACARDSTATUS') && <CodeSelect label="DVA card status" value={f.dvaCode} onChange={set('dvaCode')} items={lists.DVACardStatus} />}
        {req.has('HOUSEHOLDMANDATORY') && <CodeSelect label="Household composition" value={f.householdCode} onChange={set('householdCode')} items={lists.HouseholdComposition} />}
        <CodeSelect label="Referral source" value={f.referralSourceCode} onChange={set('referralSourceCode')} items={lists.ReferralSource} placeholder="Optional" />
        <CodeSelect label="Main reason for assistance" value={f.reasonCode} onChange={set('reasonCode')} items={lists.ReasonForAssistance} placeholder="Optional" />
        <CodeSelect label="Exit reason" value={f.exitReasonCode} onChange={set('exitReasonCode')} items={lists.ExitReason} placeholder="Still receiving services" hint="Only when they have left the service." />
      </FormRow>
      {/* Cancel closes the editor without saving: there was no way out of it but Save. */}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={saving} className="btn-ghost btn-sm">Cancel</button>
        <button type="button" onClick={save} disabled={saving} className="btn-primary btn-sm"><Save size={13} /> Save</button>
      </div>
      <DexConsents clientId={c.id} onChanged={onSaved} />
    </div>
  );
}

export default function AgedCareDex() {
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(null);         // null until chosen: then it follows the set-up state
  const [range, setRange] = useState(lastMonth());
  const [building, setBuilding] = useState(false);
  const [result, setResult] = useState(null);
  const [fixing, setFixing] = useState(null);
  const [ws, setWs] = useState(null);           // the web services connection settings
  const [logKey, setLogKey] = useState(0);
  const wsReady = !!(ws && ws.username && ws.hasPassword);

  // A failed load is shown as a failure, never as an empty set-up: an empty fallback here used to
  // tell people to upload their DEX files when the files were there and only the request had failed.
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const r = await api.get('/aged-care/dex'); setData(r.data); }
    catch (e) { setError(e.response?.data?.message || 'Could not load DEX reporting'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const build = async () => {
    setBuilding(true);
    try {
      const r = await api.post('/aged-care/dex/extract', { from: range[0], to: range[1] });
      setResult(r.data);
      toast.success(`File built: ${r.data.recordCount} participant${r.data.recordCount === 1 ? '' : 's'}, ${r.data.sessionCount} session${r.data.sessionCount === 1 ? '' : 's'}`);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not build the DEX file'); } finally { setBuilding(false); }
  };
  const download = async (x, part) => {
    try {
      const r = await api.get(`/aged-care/dex/${x.id}/file${part ? `?part=${part}` : ''}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `dex-chsp-${String(x.from_date || range[0]).slice(0, 10)}-to-${String(x.to_date || range[1]).slice(0, 10)}${x.retryOf ? '-failed-only' : ''}${part ? `-part-${part}` : ''}.xml`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not download the file'); }
  };
  // Minimum records go to DEX with most details as not stated; asked first because it changes what
  // every later report says about the person until it is switched off.
  const minimum = async (c) => {
    const ok = await confirm(`From now on ${c.name} is reported to DEX with only what the statistical linkage key needs; their other details go as not stated. Complete their record before the reporting period closes off.`,
      { title: `Report ${c.name} as a minimum record?`, confirmLabel: 'Use a minimum record' });
    if (!ok) return;
    try { await api.post(`/aged-care/dex/client/${c.id}/minimum`, { on: true }); toast.success('Reported as a minimum record from now'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };

  const d = data || EMPTY;
  const ready = d.clients.filter((c) => !c.errors.length).length;
  const mapped = d.serviceTypes.filter((t) => t.outlet_activity_id).length;
  const latest = result || d.extract;
  const notes = result || d.extract?.excluded || null;
  const filesReady = !!(d.files.reference && d.files.activities);
  const toFix = d.clients.length - ready;
  // Until a tab is chosen: set-up while the files are missing, the monthly report once they are in.
  const view = tab === 'log' && !wsReady ? 'report' : tab || (filesReady ? 'report' : 'setup');

  const head = (
    <PageHead icon={FileBarChart2}
      title="DEX reporting"
      purpose="CHSP clients, cases and sessions for the Data Exchange. CHSP reports monthly, by the 14th of the following month. Build the file here and upload it in the DEX portal under Uploads (it needs the Organisation Administrator role), or send it straight to DEX by web services."
    />
  );

  if (!data) {
    return (
      <div>
        {head}
        <div className="rounded-xl border border-slate-200 bg-white">
          {error ? <ErrorState message={error} onRetry={load} /> : (
            <>
              <p role="status" className="sr-only">Loading DEX reporting…</p>
              <TableSkeleton rows={5} cols={3} />
            </>
          )}
        </div>
      </div>
    );
  }

  const columns = [
    { key: 'name', header: 'CHSP participant', sortable: true, render: (c) => <span className="font-medium text-slate-800">{c.name}</span> },
    {
      key: 'state', header: 'DEX details', render: (c) => (
        <div className="min-w-0">
          {c.errors.length === 0
            ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={14} /> Ready</span>
            : <span className="text-red-800">Needs {c.errors.join('; ')}</span>}
          {c.warnings.length > 0 && <div className="text-[11.5px] text-amber-700">{c.warnings.join('; ')}</div>}
          {c.minimumSince && <div className="text-[11.5px] text-slate-600">Minimum record since {fmtDate(c.minimumSince)}{c.minimumDeadline ? `: complete by ${fmtDate(c.minimumDeadline)}` : ''}</div>}
          {!c.consents?.privacyNoticeOn && <div className="text-[11.5px] text-amber-700">No CHSP privacy notification recorded</div>}
          {fixing === c.id && <ClientFix c={c} lists={d.lists} required={d.requiredClientFields} onSaved={() => { setFixing(null); load(); }} onCancel={() => setFixing(null)} />}
        </div>
      ),
    },
  ];

  const TABS = [
    { id: 'setup', label: 'Set-up', icon: Settings2 },
    { id: 'participants', label: 'Participants', icon: Users, count: toFix > 0 ? toFix : undefined },
    { id: 'report', label: 'Monthly report', icon: CalendarCheck },
    wsReady && { id: 'log', label: 'Request log', icon: ScrollText },
  ];

  return (
    <div>
      {head}

      <FigureStrip>
        <Figure label="CHSP participants" value={d.clients.length} />
        <Figure label="Ready for DEX" value={ready} tone={ready < d.clients.length ? 'amber' : 'slate'} />
        <Figure label="Services mapped" value={`${mapped} of ${d.serviceTypes.length}`} tone={mapped < d.serviceTypes.length ? 'amber' : 'slate'} />
        <Figure label="Last file" value={d.extract ? fmtDate(d.extract.generatedAt) : 'None yet'} />
      </FigureStrip>

      {/* A reload that fails keeps what was already on screen and says the refresh failed. */}
      {error && (
        <p role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800">
          {error} <button type="button" className="underline ml-1" onClick={load} disabled={loading}>Try again</button>
        </p>
      )}

      <Tabs className="mb-5" value={view} onChange={setTab} ariaLabel="DEX reporting sections" tabs={TABS} />

      <div role="tabpanel" aria-label="Set-up" hidden={view !== 'setup'}>
        <DexConnection onChange={setWs} onSetupDone={() => { load(); setLogKey((k) => k + 1); }} />

        <Panel title="1. Your DEX files" icon={Upload} className="mb-5" bodyClass="px-4 py-3 space-y-2">
          <p className="text-[13px] text-slate-600">
            Download both from the DEX portal: Home, then Reference data. Every code in the upload file and every outlet activity and service type comes from them,
            so the file is never rejected for a code DEX does not know. Replace them when DSS updates the reference data or your activities change.
          </p>
          {d.files.source === 'web services' && (
            <p className="text-[12.5px] text-emerald-700"><CheckCircle2 size={13} className="inline -mt-0.5" /> Got from DEX by web services. Use Get set-up from DEX again when your activities change.</p>
          )}
          {wsReady && d.files.source !== 'web services' && <p className="text-[12.5px] text-slate-500">With the connection set up, Get set-up from DEX fetches both instead.</p>}
          <FileUpload kind="reference" label="Reference data file" uploaded={d.files.reference} detail={d.files.referenceTypes ? `${d.files.referenceTypes} code lists` : ''} onDone={load} />
          <FileUpload kind="activities" label="Organisation activity and outlets file" uploaded={d.files.activities} detail={d.files.outletCount ? `${d.files.activityCount} activities, ${d.files.outletCount} outlet activities` : ''} onDone={load} />
        </Panel>

        <Panel title="2. Map your CHSP services" icon={FileCode2} className="mb-5" bodyClass="px-4 py-3 space-y-3">
          <p className="text-[13px] text-slate-600">
            Each CHSP service type is a DEX program activity at an outlet (a case is created there), and each CHSP service is a DEX service type (set on every visit).
            The fields each service must report come from your activity file.
          </p>
          {!d.files.activities ? (
            <p className="text-[13px] text-amber-700">Upload the organisation activity and outlets file first.</p>
          ) : d.serviceTypes.length === 0 ? (
            <p className="text-[13px] text-slate-500">No CHSP visits in the last year. CHSP participants are the people enrolled on the CHSP screen.</p>
          ) : d.serviceTypes.map((t) => <MappingRow key={t.id} t={t} outlets={d.outlets} activities={d.activities} lists={d.lists} onSaved={load} />)}
          {(d.dexOutlets || []).length > 1 && <BranchOutlets outlets={d.dexOutlets} />}
        </Panel>
      </div>

      <div role="tabpanel" aria-label="Participants" hidden={view !== 'participants'}>
        <Register
          title="3. Participants" count={d.clients.length} countLabel={d.clients.length === 1 ? 'participant' : 'participants'}
          columns={columns} rows={d.clients} rowKey="id"
          rowActions={(c) => (fixing === c.id ? null : (
            <span className="flex flex-wrap justify-end gap-1">
              {c.errors.length > 0 && !c.minimumSince && (
                <button type="button" title="Their details are delayed: send what the statistical linkage key needs now, the rest as not stated, and complete it before the period closes off."
                  onClick={() => minimum(c)} className="btn-ghost btn-sm">Minimum record</button>
              )}
              {c.minimumSince && (
                <button type="button" onClick={async () => { try { await api.post(`/aged-care/dex/client/${c.id}/minimum`, { on: false }); toast.success('Full record required again'); load(); } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); } }}
                  className="btn-ghost btn-sm">Full record</button>
              )}
              <button type="button" onClick={() => setFixing(c.id)} className="btn-ghost btn-sm">{c.errors.length ? 'Fix' : 'Edit DEX details'}</button>
            </span>
          ))}
          empty={{ icon: FileBarChart2, title: 'No CHSP participants', hint: 'Enrol a participant on the CHSP screen and they appear here.' }}
          footer="A name and street address go to DEX only for someone who consented to DSS storing them; everyone else is identified by their statistical linkage key. Client numbers are our own, never a name, My Aged Care ID or Medicare number. Every CHSP client needs their My Aged Care ID on the client record. Each participant's privacy notification and consents are recorded under Fix or Edit DEX details."
        />
      </div>

      <div role="tabpanel" aria-label="Monthly report" hidden={view !== 'report'}>
        <Panel title="4. Report to DEX" icon={Download} bodyClass="px-4 py-3">
          {!filesReady && (
            <p className="mb-2 text-[13px] text-amber-700">
              Upload both DEX files, or get your set-up from DEX, before reporting.
              <button type="button" className="ml-1 font-medium underline" onClick={() => setTab('setup')}>Open set-up</button>
            </p>
          )}
          <div className="flex flex-wrap items-end gap-3">
            <FormRow cols={2}>
              <Field label="From">{(id) => <input id={id} type="date" className="input-field" value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} />}</Field>
              <Field label="To">{(id) => <input id={id} type="date" className="input-field" value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} />}</Field>
            </FormRow>
            <div className="flex flex-wrap gap-2 mb-0.5">
              <button type="button" onClick={() => setRange(lastMonth())} className="btn-ghost btn-sm">Last month</button>
              <button type="button" onClick={() => setRange(halfYear(true))} className="btn-ghost btn-sm">January to June</button>
              <button type="button" onClick={() => setRange(halfYear(false))} className="btn-ghost btn-sm">July to December</button>
            </div>
            <button type="button" onClick={build} disabled={building || !filesReady || !range[0] || !range[1]} className="btn-primary mb-0.5">
              {building ? <Loader2 size={15} className="animate-spin" /> : <FileCode2 size={15} />} Build DEX file
            </button>
          </div>

          {latest && (
            <div className="mt-4 rounded-lg border border-slate-200 p-3 space-y-2 text-[13px]">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-slate-700">
                  {latest.recordCount} participant{latest.recordCount === 1 ? '' : 's'} and {latest.sessionCount ?? 0} session{latest.sessionCount === 1 ? '' : 's'}
                  {latest.from_date ? `, ${fmtDate(latest.from_date)} to ${fmtDate(latest.to_date)}` : ''}
                </span>
                <button type="button" onClick={() => download(latest)} className="btn-secondary btn-sm"><Download size={13} /> Download XML</button>
              </div>
              {notes?.excluded?.length > 0 && (
                <p className="text-red-800"><AlertTriangle size={13} className="inline -mt-0.5" /> Left out until fixed: {notes.excluded.map((x) => `${x.name} (${x.reasons.join('; ')})`).join(' · ')}</p>
              )}
              {notes?.unmappedServices?.length > 0 && (
                <p className="text-amber-800">Not included, service not mapped: {notes.unmappedServices.map((u) => `${u.name} (${u.sessions})`).join(', ')}</p>
              )}
              {notes?.blockedSessions?.length > 0 && (
                <p className="text-amber-800">Not included: {notes.blockedSessions.map((u) => `${u.name} (${u.sessions})`).join('; ')}</p>
              )}
              {notes?.notFunded?.length > 0 && (
                <p className="text-slate-600">Not reported, not funded by CHSP: {notes.notFunded.map((u) => `${u.name} (${u.sessions})`).join('; ')}</p>
              )}
              {notes?.notDelivered > 0 && <p className="text-slate-600">Marked not delivered: {notes.notDelivered}</p>}
              {notes?.locked > 0 && <p className="text-slate-600">Left out, in a period that has closed off: {notes.locked} (record a re-opening below if DSS re-opens it)</p>}
              {notes?.sessionWarnings?.length > 0 && (
                <p className="text-amber-800">Check: {notes.sessionWarnings.map((u) => `${u.name} (${u.sessions})`).join('; ')}</p>
              )}
              <DexUpload key={latest.id} extract={latest} onDownload={download} onNewFile={(r) => { setResult(r); load(); }} />
              <p className="text-[12px] text-slate-500">
                After uploading, check the upload report in the DEX portal and record it here. Each reporting period locks 30 days after it ends (30 January and 30 July).
                Travel and admin time are not counted; visit time comes from the worker's clock-in and clock-out. A session removed after it went in a file is deleted in the DEX portal (a file cannot delete).
              </p>
            </div>
          )}
          {filesReady && <DexSessions range={range} lists={d.lists} />}
          {filesReady && <DexSend range={range} connected={wsReady} environment={ws?.environment} onSent={() => setLogKey((k) => k + 1)} />}
        </Panel>

        {filesReady && <DexMonths />}
      </div>

      {wsReady && (
        <div role="tabpanel" aria-label="Request log" hidden={view !== 'log'}>
          <DexRequestLog refreshKey={logKey} />
        </div>
      )}
    </div>
  );
}
